import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Plus, UserCog, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog.jsx"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"
import { Button } from "@/components/ui/button.tsx"

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  created_at: string
  shopify_customer_id: string | null
}

interface AdminUser {
  id: number
  email: string
  first_name: string
  last_name: string
  created_at: string
}

// Admin Users Management Component
function AdminUsersSection() {
  const { token } = useAuth()
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: ''
  })

  useEffect(() => {
    if (token) {
      fetchAdmins()
    }
  }, [token])

  const fetchAdmins = async () => {
    try {
      const response = await fetch('/api/admin/users/admins', {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch admin users')
      }

      const data = await response.json()
      setAdmins(data)
    } catch (error) {
      console.error('Error fetching admin users:', error)
      setError('Error loading admin users')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setNewAdmin(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newAdmin.email || !newAdmin.password) {
      toast.error('Email and password are required')
      return
    }
    
    try {
      setIsSubmitting(true)
      
      const response = await fetch('/api/admin/users/admins', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newAdmin.email,
          firstName: newAdmin.firstName,
          lastName: newAdmin.lastName,
          password: newAdmin.password
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to create admin user')
      }
      
      // Reset form & close dialog
      setNewAdmin({
        email: '',
        firstName: '',
        lastName: '',
        password: ''
      })
      setIsDialogOpen(false)
      
      // Show success message
      toast.success('Admin user created successfully')
      
      // Refresh admin list
      fetchAdmins()
      
    } catch (error) {
      console.error('Error creating admin user:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to create admin user')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-6">
      <Card className="shadow-sm border border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
          <div className="flex items-center">
            <UserCog className="mr-2 h-5 w-5 text-blue-600" />
            <CardTitle className="text-xl font-semibold text-gray-800">Admin Users</CardTitle>
          </div>
          <Button 
            onClick={() => setIsDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="mr-1 h-4 w-4" /> Add Admin
          </Button>
        </CardHeader>

        <CardContent className="p-0">
          {error ? (
            <div className="text-red-500 p-4">{error}</div>
          ) : isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" />
              <span>Loading admin users...</span>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              No admin users found
            </div>
          ) : (
            <div className="border-b">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="font-medium text-gray-600">Name</TableHead>
                    <TableHead className="font-medium text-gray-600">Email</TableHead>
                    <TableHead className="font-medium text-gray-600">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        {admin.first_name} {admin.last_name}
                      </TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>{new Date(admin.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Admin Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Admin User</DialogTitle>
            <DialogDescription>
              Add a new administrator user to the system.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateAdmin} className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">
                Email *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={newAdmin.email}
                onChange={handleInputChange}
                placeholder="admin@example.com"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="firstName" className="block text-sm font-medium">
                  First Name
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={newAdmin.firstName}
                  onChange={handleInputChange}
                  placeholder="First name"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="lastName" className="block text-sm font-medium">
                  Last Name
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={newAdmin.lastName}
                  onChange={handleInputChange}
                  placeholder="Last name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium">
                Password *
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                value={newAdmin.password}
                onChange={handleInputChange}
                placeholder="••••••••"
              />
              <p className="text-xs text-gray-500">
                Create a strong password (minimum 8 characters)
              </p>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                type="button"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : 'Create Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const { token } = useAuth()

  useEffect(() => {
    if (token) {
      fetchUsers()
    }
  }, [token])

  const fetchUsers = async () => {
    try {
      if (!token) {
        console.log('No token available');
        throw new Error('Authentication token is missing');
      }

      console.log('Fetching users with token:', token.substring(0, 20) + '...');

      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      if (response.status === 401) {
        console.log('Unauthorized - token might be invalid');
      }

      if (!response.ok) {
        const errorData = await response.text();
        console.log('Error response:', errorData);
        throw new Error('Failed to fetch users')
      }

      const data = await response.json()
      console.log('Fetched users:', data.length);
      setUsers(data)
    } catch (error) {
      console.error('Error fetching users:', error)
      setError('Error loading users')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteUser = async (user: User) => {
    try {
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) throw new Error('Failed to delete user');

      setUsers(users.filter(u => u.id !== user.id));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setUserToDelete(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="p-6">
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="admins">Admin Management</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
              <CardTitle className="text-xl font-semibold text-gray-800">User Management</CardTitle>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    className="pl-8 border-gray-200 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {error ? (
                <div className="text-red-500">{error}</div>
              ) : isLoading ? (
                <div className="flex items-center justify-center p-8 text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" />
                  <span>Loading users...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead className="font-medium text-gray-700">Name</TableHead>
                      <TableHead className="font-medium text-gray-700">Email</TableHead>
                      <TableHead className="font-medium text-gray-700">Role</TableHead>
                      <TableHead className="font-medium text-gray-700">Shopify ID</TableHead>
                      <TableHead className="font-medium text-gray-700">Joined</TableHead>
                      <TableHead className="font-medium text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow 
                        key={user.id}
                        className="hover:bg-gray-50 bg-white transition-colors duration-150"
                      >
                        <TableCell>
                          <div className="font-medium">
                            {user.first_name} {user.last_name}
                          </div>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <span className="capitalize">{user.role}</span>
                        </TableCell>
                        <TableCell>{user.shopify_customer_id || 'N/A'}</TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setUserToDelete(user)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete {userToDelete?.first_name} {userToDelete?.last_name}'s 
                  account and all associated prescriptions. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => userToDelete && handleDeleteUser(userToDelete)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
        
        <TabsContent value="admins">
          <AdminUsersSection />
        </TabsContent>
      </Tabs>
    </div>
  )
} 